import { Add as AddIcon, ExpandMore as ExpandMoreIcon, Remove as RemoveIcon } from '@mui/icons-material';
import CompareIcon from '@mui/icons-material/CompareArrows';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Paper,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { useState } from 'react';

/**
 * Converts HTML content to readable plain text.
 * Replaces block-level tags with newlines and strips remaining tags.
 */
function htmlToPlainText(html) {
    if (!html) return '';
    // Detect if this looks like HTML content
    if (!/<[a-z][\s\S]*>/i.test(html)) return html;
    return html
        // Block-level elements → newline before
        .replace(/<\/(div|p|li|tr|td|th|h[1-6]|blockquote|pre)>/gi, '\n')
        .replace(/<(div|p|li|tr|td|th|h[1-6]|blockquote|pre)[^>]*>/gi, '\n')
        // Line breaks
        .replace(/<br\s*\/?>/gi, '\n')
        // Strip all remaining tags
        .replace(/<[^>]+>/g, '')
        // Decode common HTML entities
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, ' ')
        // Collapse more than 2 consecutive blank lines
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

/**
 * Compute a line-by-line diff between two texts using LCS.
 * HTML content is automatically converted to plain text before diffing.
 * Returns an array of { type: 'unchanged' | 'added' | 'removed', text: string }.
 */
function computeLineDiff(oldText, newText) {
    const oldLines = htmlToPlainText(oldText || '').split('\n');
    const newLines = htmlToPlainText(newText || '').split('\n');

    const m = oldLines.length;
    const n = newLines.length;

    // Safety limit to avoid O(m*n) blow-up on very large files
    if (m * n > 500000) {
        return [
            { type: 'info', text: `Files are too large for detailed comparison (${m} vs ${n} lines). Showing summary only.` },
        ];
    }

    // Build LCS DP table
    const dp = Array.from({ length: m + 1 }, () => new Uint32Array(n + 1));
    for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
            if (oldLines[i - 1] === newLines[j - 1]) {
                dp[i][j] = dp[i - 1][j - 1] + 1;
            } else {
                dp[i][j] = dp[i - 1][j] > dp[i][j - 1] ? dp[i - 1][j] : dp[i][j - 1];
            }
        }
    }

    // Backtrack
    const result = [];
    let i = m;
    let j = n;
    while (i > 0 || j > 0) {
        if (i > 0 && j > 0 && oldLines[i - 1] === newLines[j - 1]) {
            result.unshift({ type: 'unchanged', text: oldLines[i - 1] });
            i--;
            j--;
        } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
            result.unshift({ type: 'added', text: newLines[j - 1] });
            j--;
        } else {
            result.unshift({ type: 'removed', text: oldLines[i - 1] });
            i--;
        }
    }

    return result;
}

/**
 * Compute a word-level diff between two strings.
 * Returns { oldTokens, newTokens } where each token is { type: 'same'|'changed', text }.
 * Returns null if the inputs are too large or too similar to bother.
 */
function computeWordDiff(oldText, newText) {
    // Quick exit: identical lines don't need word diff
    if (oldText === newText) return null;

    // Tokenize into alternating word/whitespace chunks
    const tokenize = (text) => text.match(/\S+|\s+/g) || [];
    const oldTokens = tokenize(oldText);
    const newTokens = tokenize(newText);
    const m = oldTokens.length;
    const n = newTokens.length;

    // Skip word diff for very large lines to avoid performance hit
    if (m * n > 200000) return null;

    // LCS DP on word tokens
    const dp = Array.from({ length: m + 1 }, () => new Uint16Array(n + 1));
    for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
            dp[i][j] = oldTokens[i - 1] === newTokens[j - 1]
                ? dp[i - 1][j - 1] + 1
                : Math.max(dp[i - 1][j], dp[i][j - 1]);
        }
    }

    // Backtrack
    const oldResult = [];
    const newResult = [];
    let i = m;
    let j = n;
    while (i > 0 || j > 0) {
        if (i > 0 && j > 0 && oldTokens[i - 1] === newTokens[j - 1]) {
            oldResult.unshift({ type: 'same', text: oldTokens[i - 1] });
            newResult.unshift({ type: 'same', text: newTokens[j - 1] });
            i--; j--;
        } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
            newResult.unshift({ type: 'changed', text: newTokens[j - 1] });
            j--;
        } else {
            oldResult.unshift({ type: 'changed', text: oldTokens[i - 1] });
            i--;
        }
    }

    return { oldTokens: oldResult, newTokens: newResult };
}

/**
 * Annotate adjacent removed+added pairs in the diff with word-level tokens.
 * Modifies a copy of the diff array in-place with a `wordTokens` field.
 */
function annotateWordDiffs(diff) {
    const result = diff.map((d) => ({ ...d }));
    for (let i = 0; i < result.length - 1; i++) {
        if (result[i].type === 'removed' && result[i + 1].type === 'added') {
            const wd = computeWordDiff(result[i].text, result[i + 1].text);
            if (wd) {
                result[i].wordTokens = wd.oldTokens;
                result[i + 1].wordTokens = wd.newTokens;
            }
        }
    }
    return result;
}

/** Number of unchanged context lines to show on each side of a change block. */
const CONTEXT_LINES = 3;

/**
 * Groups the flat diff array into display segments:
 *   { type: 'lines', items: [...] }   — a block of visible lines
 *   { type: 'collapsed', count, id }  — a run of hidden unchanged lines
 */
function buildSegments(diff) {
    const n = diff.length;
    const visible = new Array(n).fill(false);

    // Mark CONTEXT_LINES around every changed line
    for (let i = 0; i < n; i++) {
        if (diff[i].type !== 'unchanged') {
            const lo = Math.max(0, i - CONTEXT_LINES);
            const hi = Math.min(n - 1, i + CONTEXT_LINES);
            for (let j = lo; j <= hi; j++) visible[j] = true;
        }
    }

    const segments = [];
    let i = 0;
    while (i < n) {
        if (visible[i]) {
            const start = i;
            while (i < n && visible[i]) i++;
            segments.push({ type: 'lines', items: diff.slice(start, i) });
        } else {
            const start = i;
            while (i < n && !visible[i]) i++;
            const count = i - start;
            segments.push({ type: 'collapsed', count, id: `c-${start}`, items: diff.slice(start, i) });
        }
    }
    return segments;
}

function DiffSummaryChips({ diff }) {
    const added = diff.filter((d) => d.type === 'added').length;
    const removed = diff.filter((d) => d.type === 'removed').length;
    const unchanged = diff.filter((d) => d.type === 'unchanged').length;
    return (
        <Stack direction="row" spacing={1} alignItems="center">
            {added > 0 && (
                <Chip
                    icon={<AddIcon />}
                    label={`+${added} line${added !== 1 ? 's' : ''}`}
                    size="small"
                    sx={{ backgroundColor: 'rgba(76,175,80,0.12)', color: '#2e7d32', fontWeight: 600 }}
                />
            )}
            {removed > 0 && (
                <Chip
                    icon={<RemoveIcon />}
                    label={`-${removed} line${removed !== 1 ? 's' : ''}`}
                    size="small"
                    sx={{ backgroundColor: 'rgba(244,67,54,0.12)', color: '#c62828', fontWeight: 600 }}
                />
            )}
            {added === 0 && removed === 0 && (
                <Chip
                    label={`${unchanged} line${unchanged !== 1 ? 's' : ''} — identical`}
                    size="small"
                    sx={{ backgroundColor: 'rgba(0,0,0,0.06)', color: 'text.secondary' }}
                />
            )}
        </Stack>
    );
}

function DiffLine({ item }) {
    let bg = 'transparent';
    let color = 'text.primary';
    let prefix = '  ';
    let borderColor = 'transparent';
    let wordHighlightBg = 'transparent';

    if (item.type === 'added') { bg = 'rgba(76,175,80,0.12)'; color = '#2e7d32'; prefix = '+'; borderColor = '#4caf50'; wordHighlightBg = 'rgba(76,175,80,0.38)'; }
    if (item.type === 'removed') { bg = 'rgba(244,67,54,0.10)'; color = '#c62828'; prefix = '−'; borderColor = '#f44336'; wordHighlightBg = 'rgba(244,67,54,0.32)'; }

    const lineContent = item.wordTokens ? (
        item.wordTokens.map((token, idx) =>
            token.type === 'changed' && token.text.trim() !== '' ? (
                <Box
                    key={idx}
                    component="span"
                    sx={{
                        backgroundColor: wordHighlightBg,
                        borderRadius: '2px',
                        px: '1px',
                    }}
                >
                    {token.text}
                </Box>
            ) : (
                token.text
            )
        )
    ) : (
        item.text || '\u00a0'
    );

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                px: 1.5,
                py: 0.3,
                backgroundColor: bg,
                borderLeft: `3px solid ${borderColor}`,
                '&:hover': { filter: 'brightness(0.97)' },
            }}
        >
            <Typography
                component="span"
                sx={{
                    minWidth: '1.4em',
                    mr: 1,
                    color,
                    fontWeight: item.type !== 'unchanged' ? 700 : 400,
                    userSelect: 'none',
                    fontFamily: 'monospace',
                    fontSize: '0.82rem',
                    lineHeight: 1.5,
                    flexShrink: 0,
                }}
            >
                {prefix}
            </Typography>
            <Typography
                component="pre"
                sx={{
                    m: 0,
                    color,
                    fontFamily: 'monospace',
                    fontSize: '0.82rem',
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    flex: 1,
                }}
            >
                {lineContent}
            </Typography>
        </Box>
    );
}

/**
 * Renders a unified diff view of two plain-text strings.
 *
 * Props:
 *   diff            – output of computeLineDiff(), or null
 *   isLoading       – boolean
 *   error           – string | null
 *   olderVersion    – version object { versionNumber, ... }
 *   newerVersion    – version object { versionNumber, ... }
 *   formatTime      – (timestamp) => { full: string, relative: string }
 */
const TextDiffViewer = ({ diff, isLoading, error, olderVersion, newerVersion, formatTime }) => {
    const [expandedIds, setExpandedIds] = useState(new Set());

    const toggleExpand = (id) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                <Stack spacing={2} alignItems="center">
                    <CircularProgress size={32} />
                    <Typography variant="body2" color="text.secondary">
                        Loading file content…
                    </Typography>
                </Stack>
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;
    }

    if (!diff || diff.length === 0 || !olderVersion || !newerVersion) {
        return (
            <Alert severity="info" icon={<CompareIcon />} sx={{ mb: 2 }}>
                Select two versions and click Compare to see differences.
            </Alert>
        );
    }

    const infoItem = diff.find((d) => d.type === 'info');
    const annotatedDiff = infoItem ? diff : annotateWordDiffs(diff);
    const segments = infoItem ? null : buildSegments(annotatedDiff);
    const hasChanges = diff.some((d) => d.type === 'added' || d.type === 'removed');

    return (
        <Box>
            {/* Version header row */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} justifyContent="space-between" alignItems={{ sm: 'center' }}>
                <Box>
                    <Typography variant="subtitle2" color="text.secondary">Older</Typography>
                    <Typography variant="body2" fontWeight={600}>
                        v{olderVersion.versionNumber}
                        {olderVersion.createdAt && formatTime && (
                            <Tooltip title={formatTime(olderVersion.createdAt).relative}>
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    {formatTime(olderVersion.createdAt).full}
                                </Typography>
                            </Tooltip>
                        )}
                    </Typography>
                </Box>

                {!infoItem && <DiffSummaryChips diff={diff} />}

                <Box sx={{ textAlign: { sm: 'right' } }}>
                    <Typography variant="subtitle2" color="text.secondary">Newer</Typography>
                    <Typography variant="body2" fontWeight={600}>
                        v{newerVersion.versionNumber}
                        {newerVersion.createdAt && formatTime && (
                            <Tooltip title={formatTime(newerVersion.createdAt).relative}>
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    {formatTime(newerVersion.createdAt).full}
                                </Typography>
                            </Tooltip>
                        )}
                    </Typography>
                </Box>
            </Stack>

            {infoItem ? (
                <Alert severity="warning">{infoItem.text}</Alert>
            ) : !hasChanges ? (
                <Alert severity="success">Files are identical — no differences found.</Alert>
            ) : (
                <Paper variant="outlined" sx={{ borderRadius: '8px', overflow: 'hidden' }}>
                    {segments.map((seg, segIdx) => {
                        if (seg.type === 'lines') {
                            return (
                                <Box key={segIdx}>
                                    {seg.items.map((item, lineIdx) => (
                                        <DiffLine key={lineIdx} item={item} />
                                    ))}
                                </Box>
                            );
                        }

                        // Collapsed segment
                        const isExpanded = expandedIds.has(seg.id);
                        return (
                            <Box key={seg.id}>
                                {isExpanded ? (
                                    seg.items.map((item, lineIdx) => (
                                        <DiffLine key={lineIdx} item={item} />
                                    ))
                                ) : (
                                    <Box
                                        onClick={() => toggleExpand(seg.id)}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            px: 2,
                                            py: 0.6,
                                            backgroundColor: 'rgba(0,0,0,0.03)',
                                            borderTop: '1px solid',
                                            borderBottom: '1px solid',
                                            borderColor: 'divider',
                                            cursor: 'pointer',
                                            color: 'text.secondary',
                                            fontSize: '0.78rem',
                                            fontFamily: 'monospace',
                                            userSelect: 'none',
                                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)', color: 'primary.main' },
                                        }}
                                    >
                                        <ExpandMoreIcon sx={{ fontSize: '1rem' }} />
                                        {seg.count} unchanged line{seg.count !== 1 ? 's' : ''} — click to expand
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                    {/* End-of-diff marker */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            px: 2,
                            py: 1,
                            backgroundColor: 'rgba(0,0,0,0.025)',
                            borderTop: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Box sx={{ flex: 1, height: '1px', backgroundColor: 'divider' }} />
                        <Typography variant="caption" color="text.disabled" sx={{ whiteSpace: 'nowrap', userSelect: 'none' }}>
                            end of comparison
                        </Typography>
                        <Box sx={{ flex: 1, height: '1px', backgroundColor: 'divider' }} />
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

export { computeLineDiff };
export default TextDiffViewer;
